import React from "react";
import { motion } from "framer-motion";

function Module5() {
  return (
    <>
      <div className="bg-particles" />
      <div className="page-container">
        <motion.div
          className="module-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="module-number">Módulo 05</p>
          <h1 className="module-title">Memoria Agéntica</h1>
          <p className="module-description">
            Dale memoria a Comfamito con AgentCore Memory. Short Term Memory para la conversacion
            actual y Long Term Memory para aprender preferencias del usuario.
          </p>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2>🧠 ¿Qué es Memoria Agéntica?</h2>
          <p>
            La memoria agentica permite que los agentes de IA recuerden información entre
            interacciones. Sin memoria, cada conversacion empieza desde cero. Con memoria,
            el agente puede personalizar respuestas y mejorar con el tiempo.
          </p>
          <div className="architecture-diagram">
            <div className="flow">
              <div className="node">STM (Sesion)</div>
              <div className="arrow">←→</div>
              <div className="node">Agente Comfamito</div>
              <div className="arrow">←→</div>
              <div className="node">LTM (Persistente)</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2>⚡ Short Term Memory (STM)</h2>
          <p>
            La memoria de corto plazo mantiene el contexto de la conversacion actual.
            Permite que el agente recuerde lo que se dijo hace 5 mensajes sin
            enviar todo el historial al LLM.
          </p>
          <div className="code-block">
{`# Short Term Memory con AgentCore
from bedrock_agentcore.memory import MemorySession, ConversationalMessage, MessageRole

session = MemorySession(
    session_id="user-12345-session-1",
    agent_id="comfamito-prod"
)

# Guardar turnos de la conversacion
session.add_turns([
    ConversationalMessage("Hola, que beneficios tengo?", MessageRole.USER),
    ConversationalMessage("Hola! Como afiliado tienes...", MessageRole.ASSISTANT),
])

# Recuperar ultimos K turnos para contexto
recent = session.get_last_k_turns(k=5)
print(f"Ultimos {len(recent)} turnos recuperados")`}
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2>🗄️ Long Term Memory (LTM)</h2>
          <p>
            La memoria de largo plazo persiste información entre sesiones. Comfamito
            puede recordar las preferencias del usuario, historico de consultas,
            y patrones de comportamiento.
          </p>
          <div className="code-block">
{`# Long Term Memory con AgentCore
# Busqueda semantica de memorias pasadas

memories = session.search_long_term_memories(
    query="que actividades recreativas le gustan al usuario",
    namespace_prefix="/preferences/user-12345/",
    top_k=5
)

for memory in memories:
    print(f"Memoria: {memory.content}")
    print(f"Relevancia: {memory.score}")

# Procesamiento automático con LLM
# El LLM decide que guardar como memoria a largo plazo
memories, response, event = session.process_turn_with_llm(
    user_input="Me encantan los cursos de programacion y el parque Tutukan",
    llm_callback=my_llm_function,
    retrieval_config={
        "user/preferences/{actorId}/": {
            "top_k": 3
        }
    }
)

# AgentCore automáticamente extrae y guarda:
# - "Al usuario le gustan los cursos de programacion"
# - "El usuario disfruta del parque Tutukan"`}
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h2>🎯 Caso Comfama: Personalizacion</h2>
          <div className="highlight-box">
            <h4>Comfamito que Aprende</h4>
            <p>
              Imagina un afiliado que siempre pregunta por eventos para ninos.
              Con LTM, Comfamito puede proactivamente sugerir nuevos eventos familiares,
              recordar las edades de los hijos, y personalizar las recomendaciones
              de beneficios y subsidios.
            </p>
          </div>
          <ul>
            <li><strong>Preferencias:</strong> "Al usuario le gustan actividades al aire libre"</li>
            <li><strong>Historial:</strong> "Ultimo subsidio consultado: vivienda (hace 2 meses)"</li>
            <li><strong>Patrones:</strong> "Consulta beneficios educativos cada enero"</li>
            <li><strong>Contexto familiar:</strong> "Tiene 2 hijos en edad escolar"</li>
          </ul>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <h2>📁 Archivos del Modulo</h2>
          <div className="code-block">
{`ejemplos/05_memoria_agentica/
├── README.md                         # Guia paso a paso
├── 01_setup_memory_agent.py          # Configura Comfamito con memoria
├── 02_demo_stm_ltm.py               # Demo de STM y LTM en accion
└── requirements.txt                  # Dependencias`}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <div className="source-links-banner">
            <h4>📖 Documentacion Oficial AWS</h4>
            <div className="source-links-list">
              <div className="source-link-item">
                <span>AgentCore Memory →</span>
                <a href="https://docs.aws.amazon.com/bedrock/latest/userguide/agentcore-memory.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/bedrock/latest/userguide/agentcore-memory.html
                </a>
              </div>
              <div className="source-link-item">
                <span>Bedrock Agents →</span>
                <a href="https://docs.aws.amazon.com/bedrock/latest/userguide/agents.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/bedrock/latest/userguide/agents.html
                </a>
              </div>
              <div className="source-link-item">
                <span>DynamoDB →</span>
                <a href="https://docs.aws.amazon.com/amazondynamodb/latest/developerguide/Introduction.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/amazondynamodb/latest/developerguide/Introduction.html
                </a>
              </div>
              <div className="source-link-item">
                <span>OpenSearch Serverless →</span>
                <a href="https://docs.aws.amazon.com/opensearch-service/latest/developerguide/serverless.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/opensearch-service/latest/developerguide/serverless.html
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}

export default Module5;
